import { useState, useRef } from 'react';
import FileUpload from 'components/FileUpload';
import DownloadBox from 'components/DownloadBox';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import ToolSeoContent from 'components/ToolSeoContent';

// PDF to Word — v2: converts ENTIRELY IN THE BROWSER (no server).
// - pdf.js extracts text with positions, page by page (memory safe)
// - lines are grouped into paragraphs; larger fonts become headings
// - the docx library builds a real .docx file
// Benefits: fast (no server wake-up), private (nothing uploaded),
// works on large PDFs (25, 50, 100+ pages), tiny output files.

const PDFJS_URL   = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js';
const PDFJS_WORKER = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
const DOCX_URL    = 'https://cdn.jsdelivr.net/npm/docx@8.5.0/build/index.umd.js';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { if (existing.dataset.loaded) return resolve(); existing.addEventListener('load', resolve); existing.addEventListener('error', reject); return; }
    const sc = document.createElement('script');
    sc.src = src;
    sc.onload = () => { sc.dataset.loaded = '1'; resolve(); };
    sc.onerror = () => reject(new Error('load failed: ' + src));
    document.body.appendChild(sc);
  });
}

// ---- Extraction: text items -> lines (grouped by Y) -> clean strings ----
function itemsToLines(items) {
  const cleaned = items
    .filter(it => it.str && it.str.trim())
    .map(it => ({
      str: it.str,
      x: it.transform[4],
      y: it.transform[5],
      size: Math.hypot(it.transform[0], it.transform[1]) || 12,
    }));

  cleaned.sort((a, b) => b.y - a.y || a.x - b.x);

  const lines = [];
  for (const it of cleaned) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(last.y - it.y) < Math.max(3, it.size * 0.5)) {
      last.items.push(it);
      last.size = Math.max(last.size, it.size);
    } else {
      lines.push({ y: it.y, size: it.size, items: [it] });
    }
  }

  return lines.map(ln => {
    ln.items.sort((a, b) => a.x - b.x);
    let text = '';
    let prevEnd = null;
    for (const it of ln.items) {
      if (prevEnd !== null && it.x - prevEnd > it.size * 0.4) text += ' ';
      text += it.str;
      prevEnd = it.x + it.str.length * it.size * 0.5;
    }
    return { text: text.replace(/\s+/g, ' ').trim(), y: ln.y, size: ln.size };
  }).filter(l => l.text);
}

// ---- Lines -> paragraphs (gap + font-size based, list items kept separate) ----
function linesToParagraphs(pages) {
  const sizes = pages.flat().map(l => l.size).sort((a, b) => a - b);
  const body = sizes[Math.floor(sizes.length / 2)] || 12;

  const paras = [];
  pages.forEach((lines, pi) => {
    if (pi > 0) paras.push({ pageBreak: true });
    let buf = [];
    let bufSize = body;
    let prevY = null;
    const flush = () => {
      if (!buf.length) return;
      paras.push({ text: buf.join(' '), heading: bufSize > body * 1.15 });
      buf = [];
    };
    for (const ln of lines) {
      const gap = prevY === null ? 0 : prevY - ln.y;
      const isHeading = ln.size > body * 1.15;
      const bufIsHeading = bufSize > body * 1.15;
      const isListItem = /^(\d+[.)]|[a-z][.)]|[•▪‣◦*-])\s/i.test(ln.text);
      if (buf.length && (gap > ln.size * 1.8 || isHeading !== bufIsHeading || isListItem)) flush();
      buf.push(ln.text);
      bufSize = ln.size;
      prevY = ln.y;
    }
    flush();
  });
  return paras;
}

export default function PDFToWord() {
  const [file, setFile]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [dlUrl, setDlUrl]       = useState('');
  const [filename, setFilename] = useState('converted.docx');
  const [loadMsg, setLoadMsg]   = useState('');
  const [progress, setProgress] = useState(0);
  const blobUrlRef              = useRef(null);

  const reset = () => {
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    setFile(null); setError(''); setDlUrl('');
    setLoadMsg(''); setProgress(0);
  };

  const handle = async () => {
    if (!file) { setError('Please select a PDF file.'); return; }
    setLoading(true); setError(''); setDlUrl(''); setProgress(0);

    try {
      // 1) Load libraries (cached after first use)
      setLoadMsg('Loading converter...'); setProgress(4);
      await loadScript(PDFJS_URL);
      await loadScript(DOCX_URL);
      const pdfjsLib = window.pdfjsLib;
      const docx = window.docx;
      if (!pdfjsLib || !docx) throw new Error('Libraries failed to load');
      pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;

      // 2) Open the PDF
      setLoadMsg('Reading your PDF...'); setProgress(8);
      const data = new Uint8Array(await file.arrayBuffer());
      const pdf = await pdfjsLib.getDocument({ data }).promise;
      const total = pdf.numPages;

      // 3) Extract text page by page (real progress, memory freed each page)
      const pages = [];
      for (let p = 1; p <= total; p++) {
        setLoadMsg(`Extracting text — page ${p} of ${total}...`);
        setProgress(8 + Math.round((p / total) * 70));
        const page = await pdf.getPage(p);
        const content = await page.getTextContent();
        pages.push(itemsToLines(content.items));
        page.cleanup();
        // Give the browser a breath so the UI stays responsive on big PDFs
        if (p % 5 === 0) await new Promise(r => setTimeout(r, 0));
      }
      pdf.destroy();

      // 4) Scanned-PDF check: almost no text found
      const totalChars = pages.flat().reduce((n, l) => n + l.text.length, 0);
      if (totalChars < 40) {
        throw Object.assign(new Error('scanned'), { scanned: true });
      }

      // 5) Build paragraphs and the Word document
      setLoadMsg('Building Word document...'); setProgress(84);
      const paras = linesToParagraphs(pages);

      const children = paras.map(pr => {
        if (pr.pageBreak) {
          return new docx.Paragraph({ children: [], pageBreakBefore: true });
        }
        return new docx.Paragraph({
          heading: pr.heading ? docx.HeadingLevel.HEADING_2 : undefined,
          spacing: { after: 160 },
          children: [new docx.TextRun({ text: pr.text, size: pr.heading ? 28 : 22 })],
        });
      });

      const doc = new docx.Document({ sections: [{ children }] });
      setLoadMsg('Almost done...'); setProgress(94);
      const blob = await docx.Packer.toBlob(doc);

      // 6) Offer the download
      const outName = file.name.replace(/\.pdf$/i, '') + '.docx';
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setFilename(outName);
      setProgress(100); setLoadMsg('Done!');
      setTimeout(() => setDlUrl(url), 300);
    } catch (err) {
      if (err && err.scanned) {
        setError('This PDF looks like a scanned document (images of text, not real text). Text extraction is not possible for scanned PDFs.');
      } else if (err && /password/i.test(err.message || '')) {
        setError('This PDF is password-protected. Please remove the password and try again.');
      } else {
        setError('Conversion failed. Please make sure the file is a valid PDF and try again.');
      }
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Seo title="PDF to Word Converter Free" description="Convert PDF to editable Word document online, free. Fast, private — your file never leaves your device. No signup." path="/tools/pdf/to-word" />
    <div className="tool-pg">
      <div className="container" style={{ maxWidth: 720 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon"><Icon name="word" size={24} /></div>
            <div>
              <h1>PDF to Word</h1>
              <p>Convert your PDF into an editable Word (.docx) file — right in your browser. Nothing is uploaded.</p>
            </div>
          </div>
        </div>

        {!dlUrl ? (
          <div className="tool-box">
            <FileUpload
              accept=".pdf,application/pdf"
              onFileChange={setFile}
              hint="PDF files only · Works with large PDFs (100+ pages)"
            />

            {file && !loading && (
              <div className="alert alert-info" style={{ marginTop: 14 }}>
                <Icon name="info" size={16} />
                <span>Selected: <strong>{file.name}</strong> — Will convert to <strong>.docx</strong></span>
              </div>
            )}

            {error && (
              <div className="alert alert-error" style={{ marginTop: 14 }}>
                <Icon name="alertCircle" size={16} />{error}
              </div>
            )}

            <button
              className="btn btn-primary btn-lg btn-full"
              style={{ marginTop: 20 }}
              onClick={handle}
              disabled={loading || !file}
            >
              {loading
                ? <><span className="spinner sp-sm" />Converting to Word...</>
                : <><Icon name="word" size={18} />Convert PDF to Word</>
              }
            </button>

            {loading && (
              <div style={{ marginTop: 16 }}>
                {/* Progress Bar */}
                <div style={{
                  width: '100%',
                  height: 8,
                  background: 'var(--slate-200)',
                  borderRadius: 999,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${progress}%`,
                    background: 'var(--blue)',
                    borderRadius: 999,
                    transition: 'width 0.15s linear',
                  }} />
                </div>

                {/* Percent + Message */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 6,
                }}>
                  <p style={{ color: 'var(--slate-400)', fontSize: 13, margin: 0 }}>
                    ⏳ {loadMsg}
                  </p>
                  <p style={{ color: 'var(--slate-500)', fontSize: 13, fontWeight: 600, margin: 0 }}>
                    {Math.round(progress)}%
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <DownloadBox url={dlUrl} filename={filename} onReset={reset} />
        )}

        <div className="alert alert-info" style={{ marginTop: 14 }}>
          <Icon name="info" size={16} />
          <span>
            Your PDF is converted <strong>entirely in your browser</strong> — the file is never uploaded
            to any server. The result is an editable <strong>.docx</strong> that opens in Microsoft Word
            and Google Docs. Note: scanned PDFs (photos of pages) cannot be converted to text.
          </span>
        </div>
        <ToolSeoContent tool="pdf-to-word" />
      </div>
    </div>
  </>
  );
}